import { Layout } from '@/components/Layout'
import { useThemeColors } from '@/hooks/useThemeColors'
import { FONT_BODY, FONT_HEADING } from '@/config/theme'

interface LegalDocumentProps {
  /** Build-time-generated HTML (from src/legal/*.md via scripts/build-legal.mjs). Author-controlled. */
  html: string
  /** e.g. "Terms of Service" */
  title: string
  /** e.g. "1.1" */
  version: string
  /** e.g. "July 1, 2026" */
  lastUpdated: string
}

/**
 * Renders one of the legally-binding documents. The `html` comes from our own committed markdown
 * converted at BUILD time (marked, devDependency) — never user input — so dangerouslySetInnerHTML
 * has no injection surface. No markdown parser ships to the client.
 */
export function LegalDocument({ html, title, version, lastUpdated }: LegalDocumentProps) {
  const c = useThemeColors()

  return (
    <Layout>
      {/* Scoped typography for the injected document markup. */}
      <style>{`
        .legal-doc { color: ${c.tx}; font-family: ${FONT_BODY}; font-size: 14px; line-height: 1.65; }
        .legal-doc h1 { font-family: ${FONT_HEADING}; font-size: 22px; font-weight: 700; margin: 0 0 4px; color: ${c.tx}; }
        .legal-doc h2 { font-family: ${FONT_HEADING}; font-size: 17px; font-weight: 650; margin: 26px 0 8px; color: ${c.tx}; }
        .legal-doc h3 { font-family: ${FONT_HEADING}; font-size: 15px; font-weight: 600; margin: 18px 0 6px; color: ${c.tx}; }
        .legal-doc p { margin: 10px 0; }
        .legal-doc ul, .legal-doc ol { margin: 10px 0; padding-left: 22px; }
        .legal-doc li { margin: 5px 0; }
        .legal-doc a { color: ${c.ac}; text-decoration: underline; word-break: break-word; }
        .legal-doc strong { color: ${c.tx}; font-weight: 650; }
        .legal-doc hr { border: none; border-top: 1px solid ${c.dv}; margin: 22px 0; }
        .legal-doc table { border-collapse: collapse; width: 100%; margin: 12px 0; display: block; overflow-x: auto; }
        .legal-doc th, .legal-doc td { border: 1px solid ${c.dv}; padding: 6px 9px; text-align: left; }
        .legal-doc code { background: ${c.card}; border: 1px solid ${c.dv}; border-radius: 4px; padding: 1px 4px; font-size: 12px; }
      `}</style>

      <div style={{ width: '100%', maxWidth: 760, margin: '0 auto', padding: '24px 20px 8px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontFamily: FONT_HEADING, fontSize: 26, fontWeight: 700, color: c.tx, letterSpacing: -0.3 }}>
            {title}
          </div>
          <div style={{ fontSize: 12.5, color: c.mu, marginTop: 6, fontFamily: FONT_BODY }}>
            Version {version} &middot; Last updated {lastUpdated}
          </div>
        </div>

        {/* html is our own build-time-rendered, author-controlled content (see note above). */}
        <div className="legal-doc" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </Layout>
  )
}
